import { useMemo } from 'react';
import { Image, Modal, Spacer, Text, Cell, Navigation, Button, DeprecatedIcon } from '@vinted/web-ui';
import classNames from 'classnames';
import { InAppModalMessageType } from 'types/models/in-app-message';
import { linkifyString } from 'libs/utils/url';
import useBreakpoint from 'hooks/useBreakpoint';
import SeparatedList from 'components/SeparatedList/SeparatedList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

// TODO: split the component into three components:
// SplashInAppModal
// FullScreenSplashInAppModal
// CoverInAppModal
const ModalInAppMessage = _ref => {
  let {
    inAppMessage,
    isOpen,
    onClose,
    onEnter,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    onLinkClick
  } = _ref;
  const breakpoints = useBreakpoint(); // TODO: infer whether the link is external and add rel attributes

  const urlProps = inAppMessage.shouldOpenLinkInNewTab ? {
    target: '_blank'
  } : undefined;
  const isSplash = inAppMessage.type === InAppModalMessageType.Splash;
  const isCover = inAppMessage.type === InAppModalMessageType.Cover;
  const showCloseButton = useMemo(() => {
    if (inAppMessage.secondaryButtonText) {
      return Boolean(inAppMessage.primaryButtonUrl && inAppMessage.secondaryButtonUrl);
    }

    return Boolean(inAppMessage.primaryButtonUrl);
  }, [inAppMessage]);

  const handleMessageClick = event => {
    if (!(event.target instanceof HTMLAnchorElement)) return;
    const url = event.target.href;
    if (!url) return;
    onLinkClick(url);
  };

  const renderMessage = () => {
    if (!inAppMessage.message) return null;
    return /*#__PURE__*/_jsxs("div", {
      onClick: handleMessageClick,
      className: "u-text-wrap",
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsx(Text, {
        text: linkifyString(inAppMessage.message),
        html: true,
        format: true,
        theme: isCover ? 'inverse' : undefined,
        alignment: Text.Alignment.Center
      })]
    });
  };

  const renderCoverSecondaryButton = () => {
    return /*#__PURE__*/_jsx(Button, {
      onClick: onSecondaryButtonClick,
      styling: Button.Styling.Flat,
      url: inAppMessage.secondaryButtonUrl,
      urlProps: urlProps,
      testId: "in-app-message-secondary-button",
      children: /*#__PURE__*/_jsx(Text, {
        theme: "inverse",
        text: inAppMessage.secondaryButtonText,
        alignment: Text.Alignment.Center
      })
    });
  };

  const renderSecondaryButton = () => {
    if (isCover) return renderCoverSecondaryButton();
    return /*#__PURE__*/_jsx(Button, {
      text: inAppMessage.secondaryButtonText,
      onClick: onSecondaryButtonClick,
      styling: Button.Styling.Flat,
      url: inAppMessage.secondaryButtonUrl,
      urlProps: urlProps,
      testId: "in-app-message-secondary-button"
    });
  };

  const renderImage = () => {
    const classes = classNames({
      'u-fill-width u-fill-height u-position-absolute': isCover,
      'modal-in-app-message__splash-image': !isCover
    });
    return /*#__PURE__*/_jsx("div", {
      className: classes,
      children: /*#__PURE__*/_jsx(Image, {
        src: inAppMessage.imageUrl,
        scaling: Image.Scaling.Cover
      })
    });
  };

  const classes = classNames('u-justify-content-between u-flexbox u-flex-direction-column u-fill-height');
  const messageClasses = classNames({
    'u-margin-auto': isCover
  });
  const modalClasses = classNames('u-position-relative u-flexbox', {
    'u-fill-height u-fill-width': !isSplash && breakpoints.phones,
    'modal-in-app-message--cover': isCover
  });
  return /*#__PURE__*/_jsx(Modal, {
    closeOnOverlay: true,
    testId: "in-app-message-modal",
    show: isOpen,
    className: modalClasses,
    hasScrollableContent: isSplash || breakpoints.tabletsUp,
    defaultCallback: onClose,
    onAfterOpen: onEnter,
    isFramed: isSplash,
    children: /*#__PURE__*/_jsx("div", {
      className: "u-overflow-auto u-fill-width",
      children: /*#__PURE__*/_jsxs("div", {
        className: classes,
        children: [/*#__PURE__*/_jsx("div", {
          className: "u-position-absolute u-fill-width",
          children: /*#__PURE__*/_jsx(Navigation, {
            theme: "transparent",
            right: showCloseButton && /*#__PURE__*/_jsx(Button, {
              testId: "close-modal-button",
              styling: Button.Styling.Flat,
              inline: true,
              onClick: onClose,
              deprecatedIconName: "x",
              deprecatedIconColor: DeprecatedIcon.Color.GreyscaleLevel7
            })
          })
        }), renderImage(), /*#__PURE__*/_jsxs("div", {
          className: "u-justify-content-between u-flexbox u-fill-height u-flex-direction-column",
          children: [/*#__PURE__*/_jsx("div", {
            className: messageClasses,
            children: /*#__PURE__*/_jsxs(Cell, {
              theme: isCover ? 'transparent' : undefined,
              children: [inAppMessage.header && /*#__PURE__*/_jsx("div", {
                className: "u-text-wrap",
                children: /*#__PURE__*/_jsx(Text, {
                  text: inAppMessage.header,
                  type: Text.Type.Heading,
                  theme: isCover ? 'inverse' : undefined,
                  width: Text.Width.Parent,
                  alignment: Text.Alignment.Center
                })
              }), renderMessage()]
            })
          }), /*#__PURE__*/_jsx(Cell, {
            theme: isCover ? 'transparent' : undefined,
            children: /*#__PURE__*/_jsxs(SeparatedList, {
              separator: /*#__PURE__*/_jsx(Spacer, {
                size: Spacer.Size.Large
              }),
              children: [inAppMessage.primaryButtonText && /*#__PURE__*/_jsx(Button, {
                text: inAppMessage.primaryButtonText,
                onClick: onPrimaryButtonClick,
                styling: Button.Styling.Filled,
                url: inAppMessage.primaryButtonUrl,
                urlProps: urlProps,
                testId: "in-app-message-primary-button"
              }), inAppMessage.secondaryButtonText && renderSecondaryButton()]
            })
          })]
        })]
      })
    })
  });
};

export default ModalInAppMessage;